import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  Box,
  Chip,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface FilterOption {
  label: string;
  value: string | number;
}

interface FilterSelectProps {
  label: string;
  value: string | number | string[];
  onChange: (value: string | number | string[]) => void;
  options: FilterOption[];
  multiple?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  value,
  onChange,
  options,
  multiple = false,
  size = 'small',
  disabled = false,
  clearable = true,
  onClear,
}) => {
  const selectValue = multiple ? (Array.isArray(value) ? value : value ? [value as any] : []) : value;

  const handleChange = (event: SelectChangeEvent<typeof selectValue>) => {
    const v = event.target.value as unknown;
    // Ensure type matches our expected output
    if (multiple) {
      onChange(Array.isArray(v) ? v as string[] : [v as string]);
    } else {
      onChange(v as string);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClear) {
      onClear();
    } else {
      onChange(multiple ? [] : '');
    }
  };

  return (
    <FormControl
      variant="outlined"
      size={size}
      disabled={disabled}
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#ffffff',
          borderRadius: 1.5,
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
          '& fieldset': {
            borderColor: '#eef2f7',
          },
          '&:hover fieldset': {
            borderColor: '#c6d7f5',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#0b63d6',
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#44566c',
          fontSize: '0.9rem',
          fontWeight: 600,
          '&.Mui-focused': {
            color: '#0b63d6',
          },
        },
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectValue as any}
        onChange={handleChange}
        label={label}
        multiple={multiple}
        renderValue={selected => (
          multiple ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Array.from(selected as string[]).map((v) => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Box>
          ) : (selected as string)
        )}
        endAdornment={
          clearable && (multiple ? (Array.isArray(selectValue) && selectValue.length > 0) : !!selectValue) ? (
            <Box
              onClick={handleClear}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 1,
                cursor: 'pointer',
                color: '#999',
                '&:hover': {
                  color: '#1967d2',
                },
              }}
            >
              <Close sx={{ fontSize: '1.1rem' }} />
            </Box>
          ) : undefined
        }
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FilterSelect;
